import { DestroyableInjector, Injectable, Injector } from '@angular/core';
import { Overlay, OverlayConfig, PositionStrategy, ScrollStrategy } from '@angular/cdk/overlay';
import { CustomOverlayRef } from './custom-overlay-ref';
import { ComponentPortal } from '@angular/cdk/portal';

export type OverlayAvailablePositions = 'top' | 'top-left' | 'top-right' | 'bottom' | 'bottom-left' | 'bottom-right';

export interface CustomOverlayParams {
  origin: HTMLElement;
  component: any;
  positions?: OverlayAvailablePositions[];
  data?: any;
  width?: string | number;
  height?: string | number;
  hasBackdrop?: boolean;
  backdropClass?: string;
  panelClass?: string;
  scrollStrategy?: 'noop' | 'reposition' | 'close' | 'block';
}

@Injectable()
export class CustomOverlayService {
  constructor(
    private overlay: Overlay,
    private injector: Injector,
  ) {}

  open<T>(overlayInfo: CustomOverlayParams): CustomOverlayRef<T> {
    if (!overlayInfo.positions || overlayInfo.positions.length === 0) {
      overlayInfo.positions = ['bottom'];
    }
    const overlayRef = this.overlay.create(this.getOverlayConfig(overlayInfo));
    const popoverRef = new CustomOverlayRef<T>(overlayRef, overlayInfo.component, overlayInfo.data);

    const injector = this.createInjector(popoverRef, this.injector);
    overlayRef.attach(new ComponentPortal(overlayInfo.component, null, injector));

    return popoverRef;
  }

  createInjector(popoverRef: CustomOverlayRef, injector: Injector): DestroyableInjector {
    return Injector.create({
      parent: injector,
      providers: [
        {
          provide: CustomOverlayRef,
          useValue: popoverRef,
        },
      ],
    });
  }

  private getOverlayConfig(overlayInfo: CustomOverlayParams): OverlayConfig {
    return new OverlayConfig({
      hasBackdrop: !!overlayInfo.hasBackdrop,
      width: overlayInfo.width,
      height: overlayInfo.height,
      backdropClass: overlayInfo.backdropClass || 'custom-overlay-dark-backdrop',
      panelClass: overlayInfo.panelClass,
      positionStrategy: this.getOverlayPosition(overlayInfo.origin, overlayInfo.positions),
      scrollStrategy: this.getOverlayScrollStrategy(overlayInfo.scrollStrategy),
    });
  }

  private getOverlayScrollStrategy(scrollStrat: string): ScrollStrategy {
    switch (scrollStrat) {
      case 'noop':
        return this.overlay.scrollStrategies.noop();
      case 'close':
        return this.overlay.scrollStrategies.close();
      case 'block':
        return this.overlay.scrollStrategies.block();
      default:
        return this.overlay.scrollStrategies.reposition();
    }
  }

  private getOverlayPosition(origin: HTMLElement, positions: OverlayAvailablePositions[]): PositionStrategy {
    return this.overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions(this.getPositions(positions))
      .withFlexibleDimensions(false)
      .withPush(false)
      .withLockedPosition(true);
  }

  private getPositions(positions: OverlayAvailablePositions[]): any[] {
    return positions.map((pos) => {
      switch (pos) {
        case 'top':
          return {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
          };
        case 'top-left':
          return {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          };
        case 'top-right':
          return {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
          };
        case 'bottom-left':
          return {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          };
        case 'bottom-right':
          return {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          };
        default:
          return {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
          };
      }
    });
  }
}
